// Catálogo de especialidades. Centro Medico → Catálogos → Especialidades.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Especialidad, Modal, Switch, fieldCls, med } from './shared';

const vacio = { codigo: '', nombre: '', descripcion: '', duracion_min: 30, es_activo: true };

export function Especialidades() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Especialidad[]>({
    queryKey: ['med-especialidades'],
    queryFn: () => med.list<Especialidad>('especialidad', '?select=*&deleted_at=is.null&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (e?: Especialidad) => {
    if (e) { setEditId(e.id); setForm({ codigo: e.codigo ?? '', nombre: e.nombre, descripcion: e.descripcion ?? '', duracion_min: e.duracion_min, es_activo: e.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, descripcion: form.descripcion || null, duracion_min: form.duracion_min, es_activo: form.es_activo };
      return editId ? med.update('especialidad', editId, body) : med.create('especialidad', body);
    },
    onSuccess: () => { toast.success('Especialidad guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['med-especialidades'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('especialidad', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Especialidad eliminada'); void qc.invalidateQueries({ queryKey: ['med-especialidades'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Especialidades</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva especialidad</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Catálogo ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Duración</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin especialidades</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{e.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.codigo || '—'}</td>
                  <td className="py-2 pr-3">{e.duracion_min} min</td>
                  <td className="py-2 pr-3">{e.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => abrir(e)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${e.nombre}"?`, { peligro: true }))) return; eliminar.mutate(e.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar especialidad' : 'Nueva especialidad'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Duración de consulta (min)"><input type="number" min={5} className={fieldCls} value={form.duracion_min} onChange={(e) => setForm({ ...form, duracion_min: Number(e.target.value) })} /></Campo>
            <Campo label="Descripción"><AutoTextarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
